import React, { useState } from 'react'
import { ReactComponent as TrashIcon } from 'presentation/assets/icons/trash-icon.svg'
import { ReactComponent as AlertIcon } from 'presentation/assets/icons/alert.svg'
import CardNew from 'presentation/shared/components/CardNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { OpmeV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { DataSource } from 'common/enum/data-source'
import * as S from './styles'
import {
  Section,
  Item,
  QuantityAndDeleteRow,
  QuantityFieldWrapper
} from '../components/styles'
import {
  DeleteIconButton,
  StyledCheckbox,
  WarningMessage,
  JustificationWrapper
} from '../../components/styles'
import { AddItemButton, EmptyListState, SectionHeader } from '../components'

type Props = {
  initialRows: OpmeV2Props[]
  onDelete: (opmeCode: string) => void
  onQuantityChange?: (opmeCode: string, quantity: number) => void
  onToggleSelection?: (opmeCode: string) => void
  justification?: string
  onJustificationChange?: (value: string) => void
  onAddOpme?: () => void
  showAddButton?: boolean
  hideSections?: boolean
}

function OpmesList({
  initialRows,
  onDelete,
  onQuantityChange,
  onToggleSelection,
  justification = '',
  onJustificationChange,
  onAddOpme,
  showAddButton = false,
  hideSections = false
}: Props) {
  const hospitalRows = initialRows.filter(
    (row) => row.source === DataSource.TEMPLATE
  )
  const doctorRows = initialRows.filter(
    (row) => row.source === DataSource.DOCTOR
  )

  const isFromHospitalTemplate = (opmeCode: string) => {
    return hospitalRows.some((row) => row.code === opmeCode)
  }

  const shouldShowJustification = hospitalRows.some(
    (row) => row.low_acceptance && row.is_selected
  )

  const sections = hideSections
    ? [{ title: '', rows: initialRows }]
    : [
        {
          title: 'Gabarito',
          rows: hospitalRows
        },
        {
          title: 'Inclusões do médico',
          rows: doctorRows
        }
      ]

  const [editingValues, setEditingValues] = useState<{ [key: string]: string }>(
    {}
  )

  const handleQuantityChange = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    setEditingValues((prev) => ({ ...prev, [code]: numericValue }))

    if (numericValue !== '') {
      const quantity = parseInt(numericValue, 10)
      if (!isNaN(quantity) && quantity > 0 && quantity <= 99) {
        onQuantityChange?.(code, quantity)
      }
    }
  }

  const handleQuantityBlur = (code: string, value: string) => {
    const numericValue = value.replace(/\D/g, '')
    const quantity = parseInt(numericValue, 10)

    if (isNaN(quantity) || quantity <= 0 || numericValue === '') {
      onQuantityChange?.(code, 1)
    }

    setEditingValues((prev) => {
      const newValues = { ...prev }
      delete newValues[code]
      return newValues
    })
  }

  const handleQuantityFocus = (code: string, currentQuantity: number) => {
    const formattedValue =
      currentQuantity < 10 ? `0${currentQuantity}` : currentQuantity.toString()
    setEditingValues((prev) => ({ ...prev, [code]: formattedValue }))
  }

  const getDisplayQuantity = (code: string, quantity: number): string => {
    const isEditing = code in editingValues
    const displayValue = isEditing
      ? editingValues[code]
      : quantity < 10
      ? `0${quantity}`
      : quantity.toString()
    return displayValue
  }

  if (initialRows.length === 0) {
    return (
      <EmptyListState
        message="Nenhum OPME foi adicionado ainda"
        showAddButton={showAddButton}
        onAdd={onAddOpme}
        addLabel="Adicionar outro OPME"
      />
    )
  }

  return (
    <CardNew
      background="neutral25"
      padding="1rem"
      style={{ borderRadius: '8px' }}
    >
      {sections.map((section, sectionIndex) => {
        if (section.rows.length === 0) return null

        return (
          <Section key={`section-${sectionIndex}`}>
            <SectionHeader title={section.title} />
            {section.rows.map((row, rowIndex) => {
              const displayQuantity = getDisplayQuantity(
                row?.code,
                row.quantity
              )
              const isLastRow = rowIndex === section.rows.length - 1

              return (
                <div key={row?.code}>
                  <Item>
                    <S.OpmeRow>
                      <StyledCheckbox
                        checked={row.is_selected || false}
                        onChange={() => onToggleSelection?.(row?.code)}
                        disabled={!isFromHospitalTemplate(row?.code)}
                        $lowAcceptance={row.low_acceptance}
                      />
                      <SupportTextNew
                        size="xxxsmall"
                        weight="semiBold"
                        color="textPrimary"
                        as="span"
                      >
                        {row.description}
                      </SupportTextNew>
                    </S.OpmeRow>

                    <QuantityAndDeleteRow>
                      <QuantityFieldWrapper>
                        <TextFieldNew
                          labelOverlapping
                          labelSize="xxsmall"
                          labelWeight="semiBold"
                          label="Quantidade"
                          name={`quantity-${row?.code}`}
                          value={displayQuantity}
                          onChange={(e) =>
                            handleQuantityChange(row?.code, e.target.value)
                          }
                          onBlur={(e) =>
                            handleQuantityBlur(row?.code, e.target.value)
                          }
                          onFocus={() =>
                            handleQuantityFocus(row?.code, row.quantity)
                          }
                          maxLength={2}
                          height="40px"
                        />
                      </QuantityFieldWrapper>
                      <DeleteIconButton
                        size="small"
                        onClick={() => onDelete(row?.code)}
                        disabled={isFromHospitalTemplate(row?.code)}
                      >
                        <TrashIcon width={18} height={18} />
                      </DeleteIconButton>
                    </QuantityAndDeleteRow>

                    {row.low_acceptance && (
                      <WarningMessage style={{ marginTop: '0.5rem' }}>
                        <AlertIcon width={16} height={16} />
                        <span>
                          Este OPME foi negado em solicitações anteriores
                        </span>
                      </WarningMessage>
                    )}
                  </Item>
                  {!isLastRow && (
                    <DividerNew marginTop="0.75rem" marginBottom="0.75rem" />
                  )}
                </div>
              )
            })}

            {section.title === 'Gabarito' && shouldShowJustification && (
              <JustificationWrapper style={{ marginTop: '0.75rem' }}>
                <TextAreaNew
                  label="Para incluir opme que tem histórico de negativa pela operadora, necessário justificativa especial"
                  labelColor="danger700"
                  labelSize="xxxsmall"
                  labelWeight="semiBold"
                  bgColor="transparent"
                  outline
                  outlineColor="danger700"
                  outlineFocusColor="danger700"
                  placeholder="Descreva o motivo da solicitação deste(s) OPME(s)"
                  fontSize="small"
                  value={justification}
                  onInputChange={onJustificationChange}
                  rows={3}
                />
              </JustificationWrapper>
            )}
          </Section>
        )
      })}

      {showAddButton && (
        <AddItemButton onClick={onAddOpme} label="Adicionar outro OPME" />
      )}
    </CardNew>
  )
}

export default OpmesList
